import type { StatisticCardSize } from './types'

// 卡片间距 / 内部间距 / 图表在右侧时的水平间距
export const CARD_GAP = 8
export const CARD_INTERNAL_GAP = 2
export const CARD_RIGHT_PLACEMENT_GAP = 16

// 左侧标题模式下 group 标题默认占位宽度（em）
export const GROUP_DEFAULT_TITLE_WIDTH: Record<StatisticCardSize, number> = {
  large: 16,
  small: 14,
}

// 卡片最小宽度 / 高度
export const CARD_MIN_WIDTH: Record<StatisticCardSize, number> = {
  large: 180,
  small: 160,
}

export const CARD_HEIGHT: Record<StatisticCardSize, number> = {
  large: 110,
  small: 90,
}

// 主数值字号 / 子指标字号
export const VALUE_FONT_SIZE: Record<StatisticCardSize, number> = {
  large: 24,
  small: 20,
}

export const SUB_INDICATOR_FONT_SIZE: Record<StatisticCardSize, number> = {
  large: 14,
  small: 12,
}

// 选择控件在卡片内的定位偏移与尺寸
export const SELECTION_TOP_OFFSET = 4
export const SELECTION_RIGHT_OFFSET = 6
export const SELECTION_SIZE = 14

// BEM 类名前缀
export const STATISTIC_CARD_CLASS_PREFIX = 'jd-statistic-card'

export const STATISTIC_CARD_CLASSES = {
  root: STATISTIC_CARD_CLASS_PREFIX,
  header: `${STATISTIC_CARD_CLASS_PREFIX}__header`,
  titleWrap: `${STATISTIC_CARD_CLASS_PREFIX}__title-wrap`,
  title: `${STATISTIC_CARD_CLASS_PREFIX}__title`,
  titleExtra: `${STATISTIC_CARD_CLASS_PREFIX}__title-extra`,
  headerExtra: `${STATISTIC_CARD_CLASS_PREFIX}__header-extra`,
  tooltipTrigger: `${STATISTIC_CARD_CLASS_PREFIX}__tooltip-trigger`,
  value: `${STATISTIC_CARD_CLASS_PREFIX}__value`,
  valuePrefix: `${STATISTIC_CARD_CLASS_PREFIX}__value-prefix`,
  valueContent: `${STATISTIC_CARD_CLASS_PREFIX}__value-content`,
  valueSuffix: `${STATISTIC_CARD_CLASS_PREFIX}__value-suffix`,
  subIndicators: `${STATISTIC_CARD_CLASS_PREFIX}__sub-indicators`,
  subIndicator: `${STATISTIC_CARD_CLASS_PREFIX}__sub-indicator`,
  subIndicatorLabel: `${STATISTIC_CARD_CLASS_PREFIX}__sub-indicator-label`,
  subIndicatorValue: `${STATISTIC_CARD_CLASS_PREFIX}__sub-indicator-value`,
  extra: `${STATISTIC_CARD_CLASS_PREFIX}__extra`,
  chart: `${STATISTIC_CARD_CLASS_PREFIX}__chart`,
  selection: `${STATISTIC_CARD_CLASS_PREFIX}__selection`,
  listWrapper: `${STATISTIC_CARD_CLASS_PREFIX}-list`,
  listContainer: `${STATISTIC_CARD_CLASS_PREFIX}-list__container`,
  gridContainer: `${STATISTIC_CARD_CLASS_PREFIX}-grid__container`,
  pageViewport: `${STATISTIC_CARD_CLASS_PREFIX}-list__viewport`,
  pageTrack: `${STATISTIC_CARD_CLASS_PREFIX}-list__track`,
  page: `${STATISTIC_CARD_CLASS_PREFIX}-list__page`,
} as const

// 生成一条 `var(--<ns>-<name>, <fallback>)` 的 CSS 变量引用。
// cssVarName 由 useNamespace().cssVarName 提供，前缀随运行时 namespace 动态变化，不写死 jd。
type CssVarNameFn = (name: string) => string

// 与 SCSS 中 getCssVar('color-*') 完全对齐的 CSS 变量集合。
// 通过工厂在组件 setup 内基于运行时 namespace 生成，避免写死 --jd- 前缀。
// 保留 var(... , fallback) 双保险，兼容主题变量缺失场景。
export function createColorVars(cssVarName: CssVarNameFn) {
  const v = (name: string, fallback: string) => `var(${cssVarName(name)}, ${fallback})`
  const surfaceWhite = `var(${cssVarName('color-fill-white')}, ${v('color-surface-200', '#ffffff')})`
  return {
    border: v('color-line-200', '#d9d9d9'),
    borderHover: v('color-line-300', '#b5b5b5'),
    brand: v('color-brand-normal', '#3768fa'),
    brandHover: v('color-brand-hover', '#5b84ff'),
    brandDisabled: v('color-brand-disable', '#adc7fc'),
    brandLight: v('color-brand-light-hover', '#f6f9ff'),
    brandBorder: v('color-brand-border', '#d6e3fd'),
    brandTint: v('color-brand-40', 'rgba(38, 107, 246, 0.25)'),
    surface: surfaceWhite,
    fillDisabled: v('color-fill-200', '#f0f0f0'),
    textPrimary: v('color-text-400', '#262626'),
    textLabel: v('color-text-300', '#595959'),
    textSecondary: v('color-text-200', '#8c8c8c'),
    groupTitle: v('color-text-400', '#1c1d1f'),
    groupDescription: v('color-text-200', '#8c8d91'),
    trendPositive: v('color-down-normal', '#00cb7f'),
    trendNegative: v('color-up-normal', '#f33b50'),
    groupDefaultBg: v('color-fill-200', '#f2f4f7'),
    groupBrandBg: v('color-brand-background', '#eaf1fe'),
    divider: v('color-line-200', '#d9d9d9'),
    buttonGrayBg: 'rgba(0, 0, 0, 0.05)',
    buttonWhiteBg: surfaceWhite,
    buttonIconDefault: v('color-text-300', '#595959'),
    buttonIconHover: v('color-text-400', '#262626'),
    buttonIconDisabled: v('color-text-100', '#b5b5b5'),
    chartFillStart: v('color-fill-white', '#ffffff'),
    radiusCard: v('radius-4', '8px'),
    radiusXs: v('radius-xs', '4px'),
  }
}

// 由 createColorVars 生成的颜色变量集合类型，供组件标注 colorVars 使用。
export type StatisticCardColorVars = ReturnType<typeof createColorVars>

/**
 * 基于运行时 colorVars 生成趋势取色函数（前缀随 namespace 动态）。
 * 返回的 getTrendColor：
 * - 0 视为中性，展示次要文本色
 * - reverseColor 用于"越大越差"的负向指标（如流失率），涨色/跌色互换
 */
export function createGetTrendColor(colorVars: StatisticCardColorVars) {
  return (value: number, reverseColor?: boolean): string => {
    if (value === 0) {
      return colorVars.textSecondary
    }
    if (reverseColor) {
      return value > 0 ? colorVars.trendNegative : colorVars.trendPositive
    }
    return value > 0 ? colorVars.trendPositive : colorVars.trendNegative
  }
}